import { Badge, Button, Group, Paper, Select, Skeleton, Table, Text } from '@mantine/core';
import type { DatesRangeValue } from '@mantine/dates';
import type { AuditEvent } from '@motionsafe/ms-console-api/admin/v1/audit_pb.js';
import { IconDownload, IconListDetails } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { errorText, useOrgNames } from '../../api/hooks';
import { ACTION_STYLE, AuditRange, EventDetail, HAIRLINE, None, outcomeColor, PartyCell, useAuditPages } from '../../components/audit-events';
import { useGridRows } from '../../components/grid';
import { EmptyState, PageHeader, ToolbarActions } from '../../components/page';
import { PageFooter } from '../../components/pager';
import { PaneDrawer } from '../../components/pane';
import { when } from '../../lib/format';
import { ICON_INLINE } from '../../theme';
import { useOrgId, useTopLevel } from '../_app';

export const Route = createFileRoute('/_app/audit')({ component: Audit });

const GROUPS: Record<string, string[]> = {
  'Everything': [],
  'Sign-ins': ['operator.', 'admin.', 'session.'],
  'People': ['user.', 'seat.', 'restriction.', 'mapping.'],
  'Keys': ['key.', 'keyring.'],
  'Devices': ['device.', 'android_auto.'],
  'Sharing': ['collection.'],
  'Organization': ['org.', 'policy.', 'feature.', 'products.'],
};

const COLUMNS = ['When', 'Action', 'Outcome', 'Actor', 'Target', 'Source'] as const;
// Actor and target share what is left; the rest have their own width, so nothing shifts from one page to the next.
const COLUMN_WIDTH: Record<string, number> = { When: 190, Organization: 160, Action: 280, Outcome: 110, Source: 100 };
// Placeholder widths per column so loading rows read as the table to come.
const SKELETON_WIDTH = ['80%', '70%', 56, '60%', '55%', 48] as const;

export function Audit() {
  // Among MotionSafe's own pages the trail is everything: every organization's events, and those recorded against none.
  const top = useTopLevel();
  const columns = top ? ['When', 'Organization', ...COLUMNS.slice(1)] : [...COLUMNS];
  const orgId = useOrgId();
  const [group, setGroup] = useState('Everything');
  const [range, setRange] = useState<DatesRangeValue<string>>([null, null]);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [paneOpen, setPaneOpen] = useState(false);
  const { events, rows, total, page, setPage, dated, exportCsv, exporting } = useAuditPages({ top, orgId, actions: GROUPS[group] ?? [], range });
  const named = useOrgNames(rows.map((e) => e.orgId), top);
  const orgName = (id: string) => named(id) || 'unknown';
  const filtered = group !== 'Everything' || dated;
  const showTable = events.isPending || rows.length > 0;
  const open = (e: AuditEvent) => { setSelected(e); setPaneOpen(true); };
  const gridRow = useGridRows(rows.map((e) => e.id));

  return (
    <>
      <PageHeader
        title={top ? 'All audit events' : 'Audit trail'}
        description={top
          ? 'Every sign-in, change, and key event in every organization, and what MotionSafe staff did that belongs to none. Newest first.'
          : 'Every sign-in, change, and key event for this organization, newest first. Each row is chained to the one before it, so a gap would show.'}
      />

      <Group gap="sm" align="center" wrap="wrap" mb="md">
        <Select data={Object.keys(GROUPS)} value={group} onChange={(v) => { if (v) setGroup(v); }} allowDeselect={false} aria-label="Event group" w={{ base: '100%', xs: 200 }} />
        <AuditRange value={range} onChange={setRange} />
        {events.isSuccess && rows.length > 0 && (
          <Text size="sm" c="dimmed" className="num" ml="auto">
            {total === 1 ? '1 event' : `${total} events`}
          </Text>
        )}
        <ToolbarActions><Button size="sm" variant="default" leftSection={<IconDownload {...ICON_INLINE} />} onClick={() => void exportCsv()} disabled={exporting}>Export CSV</Button></ToolbarActions>
      </Group>

      <Paper p={0} style={{ overflow: 'hidden' }}>
        {showTable && (
          // The densest table in the console runs a step tighter between
          // columns, so the six of them fit a desktop window without scrolling.
          <Table.ScrollContainer minWidth={top ? 1120 : 1000}>
            <Table role="grid" layout="fixed" aria-label="Audit events" horizontalSpacing="md" style={{ opacity: events.isPlaceholderData ? 0.6 : 1, transition: 'opacity var(--motion-micro, 120ms) ease-out' }}>
              <Table.Thead>
                <Table.Tr>{columns.map((c) => <Table.Th key={c} {...(COLUMN_WIDTH[c] ? { w: COLUMN_WIDTH[c] } : {})}>{c}</Table.Th>)}</Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {events.isPending && [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <Table.Tr key={`loading-${i}`} aria-hidden="true" bg="transparent">
                    {columns.map((c, j) => <Table.Td key={c}><Skeleton height={14} width={SKELETON_WIDTH[j] ?? '60%'} my={5} /></Table.Td>)}
                  </Table.Tr>
                ))}
                {rows.map((e) => {
                  const isSelected = paneOpen && selected?.id === e.id;
                  return (
                    <Table.Tr
                      key={e.id}
                      {...gridRow(e.id, () => open(e))}
                      aria-selected={isSelected}
                      onClick={() => open(e)}
                      style={{ cursor: 'pointer' }}
                      {...(isSelected ? { bg: 'var(--mantine-primary-color-light)' } : {})}
                    >
                      <Table.Td className="num" style={{ whiteSpace: 'nowrap' }}>{when(e.occurredAt)}</Table.Td>
                      {top && <Table.Td>{e.orgId ? orgName(e.orgId) : <Text span inherit c="dimmed">MotionSafe</Text>}</Table.Td>}
                      <Table.Td><span className="id" style={ACTION_STYLE}>{e.action}</span></Table.Td>
                      <Table.Td><Badge color={outcomeColor(e.outcome)}>{e.outcome}</Badge></Table.Td>
                      <Table.Td><PartyCell party={e.actor} /></Table.Td>
                      <Table.Td><PartyCell party={e.target} /></Table.Td>
                      <Table.Td>{e.source || <None />}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {events.isSuccess && rows.length === 0 && (
          filtered
            ? <EmptyState icon={IconListDetails} title="Nothing in this range" body="No events match this group and these dates. Choose Everything or widen the range." />
            : <EmptyState icon={IconListDetails} title="Nothing recorded yet" body="The trail fills with sign-ins, invitations, and every administrative change as they happen." />
        )}
        {events.isError && (
          <Text size="sm" c="danger" ta="center" py="lg" px="lg" {...(rows.length > 0 ? { style: { borderTop: HAIRLINE } } : {})}>{errorText(events.error)}</Text>
        )}
        <PageFooter page={page} total={total} noun="events" onChange={setPage} />
      </Paper>

      <PaneDrawer opened={paneOpen && selected !== null} onClose={() => setPaneOpen(false)}>
        {selected && <EventDetail e={selected} />}
      </PaneDrawer>
    </>
  );
}
